/**
 * Was die Mitarbeiterseite einmalig anzeigt: Einrichtungscodes und
 * Einmalpasswörter.
 *
 * Eigene Datei, damit die Seite selbst kurz bleibt — der Inhalt ist
 * unverändert aus `MitarbeiterSeite.tsx` herübergezogen (HJ-010).
 */

import { Karte, Knopf, Meldung } from '../gestaltung/bausteine';

/**
 * Was einmalig angezeigt und danach nie wieder abrufbar ist.
 *
 * ═══ WARUM ES EINE LISTE IST UND KEIN EINZELNER WERT ═══
 *
 * Es gab genau EINEN Platz für den Wert. Wer nacheinander für drei Personen
 * einen Code erzeugte, überschrieb jedes Mal den vorigen — und der war
 * unwiederbringlich weg, denn gespeichert ist nur der Hash. Wer neue Leute
 * einrichtet, tut das aber selten einzeln: der Montagmorgen, drei Fahrer, drei
 * Codes.
 *
 * Neue Werte stehen VORNE (das ist, worauf man gerade schaut), und jeder Kasten
 * geht nur durch sein eigenes „Weg damit" — über die Kennung, nicht über den
 * Index, denn der verschiebt sich mit jedem neuen Wert.
 *
 * Nicht gebaut, mit Absicht: Klartextspeicherung (dann gäbe es den Wert
 * wieder, und der Hash wäre Dekoration) und „Codes für alle in einem Klick"
 * (Codes entstehen, wenn die Person daneben steht, nicht auf Vorrat — ein
 * Code, der 48 Stunden gilt, läuft sonst ab, bevor er gebraucht wird).
 */
export type Einmalwert = {
  /** Eigene Kennung — der Schlüssel für „Weg damit". */
  id: string;
  /**
   * Wofür der Wert gilt („code:<person>"). Ein neuer Wert für dieselbe Sache
   * ersetzt den alten: Ein neu erzeugter Code entwertet den vorigen, und ihn
   * weiter in der Liste stehen zu lassen hieße, einen toten Code herzuzeigen.
   */
  fuer: string;
  ueberschrift: string;
  name: string;
  wert: string;
  erklaerung: string;
  nebenzeile?: string;
  /** Die Frist in einer Zeile, für die Sammelliste — oder was sonst dazugehört. */
  frist: string;
};

/**
 * „48 Stunden" oder „14 Tage" — die Frist im Klartext.
 *
 * Prüfkonten bekommen seit dem 19.09. 14 Tage statt 48 Stunden. Der Kasten
 * liest die Frist aus der Antwort der Datenbank, also steht die richtige Zahl
 * automatisch da; dieser Text macht sie nur lesbar. Ein Datum allein zwingt
 * zum Rechnen, und bei „Gültig bis Do., 03.10." denkt man an zwei Tage.
 */
export function fristText(gueltigBis: Date): string {
  const stunden = Math.round((gueltigBis.getTime() - Date.now()) / 3_600_000);
  if (stunden > 48) return `${Math.round(stunden / 24)} Tage`;
  return `${stunden} Stunden`;
}

/* ------------------------------------------------------------ Sammelliste */

/**
 * „Alle Codes dieser Sitzung" — Name, Code, Frist je Zeile.
 *
 * Zwei Ausgaben, beide ohne Umweg über den Server: In die Zwischenablage
 * (tabulatorgetrennt, damit es in Excel oder eine Mail passt) und auf Papier.
 * Der Ausdruck blendet per `@media print` alles andere aus — sonst käme die
 * ganze Mitarbeiterliste mit aufs Blatt, samt Knöpfen.
 *
 * Diese Karte steht NICHT in einem Speicher. Sie lebt genau so lange wie die
 * Kästen: Wird die Seite verlassen, ist alles weg, und das ist gewollt.
 */
export function Sammelliste(eigenschaften: {
  werte: Einmalwert[];
  kopiert: string | null;
  setzeKopiert: (text: string | null) => void;
}) {
  async function kopieren() {
    const text = [
      ['Name', 'Code', 'Frist'].join('\t'),
      ...eigenschaften.werte.map((w) => [w.name, w.wert, w.frist].join('\t')),
    ].join('\n');
    try {
      await navigator.clipboard.writeText(text);
      eigenschaften.setzeKopiert('In die Zwischenablage kopiert.');
    } catch {
      // Ehrlich sagen, dass es nicht ging — sonst klebt jemand ein und findet
      // die alte Zwischenablage.
      eigenschaften.setzeKopiert(
        'Kopieren wurde vom Browser verweigert. Nimm „Drucken" oder schreib die Codes ab.',
      );
    }
  }

  return (
    <div className="codeliste">
      <Karte
        titel="Alle Codes dieser Sitzung"
        werkzeuge={
          <div className="reihe keindruck" style={{ gap: 8 }}>
            <Knopf art="zweit" klein onClick={() => void kopieren()}>
              In die Zwischenablage
            </Knopf>
            <Knopf art="zweit" klein onClick={() => window.print()}>
              Drucken
            </Knopf>
          </div>
        }
        blank
      >
        <table className="tabelle">
          <thead>
            <tr>
              <th>Name</th>
              <th>Code</th>
              <th>Frist</th>
            </tr>
          </thead>
          <tbody>
            {eigenschaften.werte.map((w) => (
              <tr key={w.id}>
                <td>{w.name}</td>
                <td className="code">{w.wert}</td>
                <td className="meta">{w.frist}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="meta keindruck" style={{ padding: '10px 16px' }}>
          {eigenschaften.kopiert ??
            'Nur solange diese Seite offen ist. Danach lässt sich ein Code nur neu erzeugen.'}
        </p>
      </Karte>
    </div>
  );
}

/* -------------------------------------------------------------- Einmalkasten */

export function EinmalKasten(eigenschaften: {
  wert: Einmalwert;
  schliessen: () => void;
}) {
  const { wert } = eigenschaften;
  return (
    <Meldung
      art="gut"
      titel={wert.ueberschrift}
      knopf={{ text: 'Verstanden, weg damit', tun: eigenschaften.schliessen }}
    >
      <div style={{ fontSize: 30, margin: '6px 0' }} className="code">
        {wert.wert}
      </div>
      {wert.nebenzeile && <div>{wert.nebenzeile}</div>}
      <strong>Das wird nur jetzt angezeigt.</strong> {wert.erklaerung}
    </Meldung>
  );
}
